<template>
    <div v-if="canReadCategory" class="left-side-main-menu"
        :class="{ 'has-overflow scrolling': currentSlide > 0, 'd-none': categories.length == 0 }">
        <Carousel ref="carousel" class="nav-menu-card-container scroller-hide horizontal-scroll" :items-to-show="4"
            :wrap-around="false" :snap-align="'start'" :breakpoints="dynamicBreakpoints" @slide-end="checkSlide">
            <CategoryTab v-for="category in categories" :key="category.name" :category="category"
                @update:category="selectCategory" />
        </Carousel>
    </div>
</template>

<script setup>
import { computed, defineEmits, onMounted, ref } from 'vue';
import eventBus from '@/eventBus';
import { Carousel } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import { useCategoryStore } from '@/stores/category';
import { useDeviceVerifierStore } from '@/stores/deviceVerifier';
import { useCategory } from "@/composables/useCategory";
import { useSystemSettingsStore } from '@/stores/systemSettings';
import CategoryTab from '@/components/order/categoryTab.vue';
// import { get } from 'core-js/core/dict';

const emit = defineEmits(['update:category']);

const { getSidebarState } = useSystemSettingsStore();
const { canReadCategory, fetchCategories } = useCategory();
const { getIsVerified } = useDeviceVerifierStore();
const { setActiveCategory } = useCategoryStore();

const isSidebarOpen = computed(() => getSidebarState());

const categories = ref([]);
const currentSlide = ref(0);
const carousel = ref(null);

/**
 * Carousel Breakpoints Configuration
 *
 * These breakpoints control how many category cards ("itemsToShow") are visible in the carousel
 * at different viewport widths. The values are based on the minimum width of each card, which is
 * set by the .nav-menu-card CSS class (min-width: 120px). If you change the min-width of .nav-menu-card,
 * you should adjust these values to fit more or fewer cards per row accordingly.
 *
 * The breakpoints are linked to the .nav-menu-card layout as follows:
 * - The "itemsToShow" value determines how many .nav-menu-card elements are visible in the carousel at once.
 * - For example, with min-width: 120px and a viewport width of 1024px, you can fit more cards than with 170px.
 * - The "snapAlign: 'start'" ensures the first card aligns to the start of the carousel.
 *
 * There are two sets of breakpoints:
 * 1. baseBreakpoints: Used when the sidebar is closed (more horizontal space is available).
 *    - 1024px and above: Show up to 10 cards at a time.
 *    - 567px and above: Show up to 7 cards at a time.
 * 2. dynamicBreakpoints: Used when the sidebar is open (less horizontal space available).
 *    - 1024px and above: Show 7 cards.
 *    - 567px and above: Show 4 cards.
 *
 * This dynamic adjustment ensures that .nav-menu-card items are never squeezed and the layout remains visually balanced.
 */
const baseBreakpoints = {
    1024: { itemsToShow: 10, snapAlign: 'start' }, // for wide screens, show 10 cards
    567: { itemsToShow: 7, snapAlign: 'start' },   // for medium screens, show 7 cards
};

/**
 * dynamicBreakpoints is necessary to make the carousel responsive to layout changes,
 * specifically when the sidebar is open or closed. When the sidebar is open, there is
 * less horizontal space available for the category cards, so we reduce the number of
 * visible cards to prevent them from being squeezed or cut off. When the sidebar is
 * closed, we can show more cards. This ensures the UI remains visually balanced and
 * user-friendly regardless of sidebar state.
 */
const dynamicBreakpoints = computed(() => {
    if (isSidebarOpen.value) {
        // Sidebar open: less space, so show fewer cards
        return {
            1024: { itemsToShow: 7, snapAlign: 'start' },
            567: { itemsToShow: 4, snapAlign: 'start' },
        };
    }
    // Sidebar closed: use the base (maximum) breakpoints
    return baseBreakpoints;
});

const selectCategory = (categoryName) => {
    setActiveCategory(categoryName);
    emit('update:category', categoryName);
    // Emit event to trigger initializeWithRetry in foodItemsGrid.vue
    eventBus.emit('categoryChanged');
};

const checkSlide = (data) => {
    currentSlide.value = data.currentSlide;
};

onMounted(async () => {
    if (getIsVerified()) {
        try {
            const response = await fetchCategories();
            categories.value = response.filter(category => category.product_count > 0);
            if (categories.value.length > 0) selectCategory(categories.value[0]?.name ?? '');
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    }
});
</script>

<style scoped>
:deep(.carousel__slide) {
    margin-right: 10px;
}

:deep(.carousel__track) {
    display: flex;
    gap: 0;
}
</style>